/**
 * MessageModal
 * Dismissable centered dialog for in-app message announcements.
 * All text resolved via i18n keys from the message's i18nKey prefix.
 */

import React from 'react';
import { Modal, TouchableOpacity, StyleSheet, View } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_SM, FS_MD, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { resolveMessageTexts } from '../../utils/messageI18n';
import { openURL } from '../../utils/linking';
import type { InAppMessagePayload } from '../../generated/api/http-contracts';
import { useMessageStore } from '../../store/messageStore';

interface Props {
  message: InAppMessagePayload;
}

export function MessageModal({ message }: Props) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const dismissMessage = useMessageStore((s) => s.dismissMessage);
  const texts = resolveMessageTexts(message.i18nKey);

  const handleAction = () => {
    if (message.actionPayload) {
      void openURL(message.actionPayload);
    }
    if (message.dismissable) {
      dismissMessage(message.id);
    }
  };

  const handleDismiss = () => {
    dismissMessage(message.id);
  };

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={message.dismissable ? handleDismiss : undefined}
    >
      <View
        testID="message-modal-overlay"
        style={[styles.overlay, { backgroundColor: theme.overlay }]}
      >
        <View
          testID="message-modal"
          style={[styles.card, { backgroundColor: theme.card }]}
        >
          {texts.title !== '' && (
            <Text
              testID="message-modal-title"
              allowFontScaling={false}
              style={[styles.title, { fontSize: FS_LG * fontScale, color: theme.text, ...ffs }]}
            >
              {texts.title}
            </Text>
          )}

          {texts.body !== '' && (
            <Text
              testID="message-modal-body"
              allowFontScaling={false}
              style={[styles.body, { fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }]}
            >
              {texts.body}
            </Text>
          )}

          <View style={styles.buttons}>
            {message.dismissable && (
              <TouchableOpacity
                testID="message-modal-dismiss"
                accessibilityLabel={t('common.dismiss')}
                accessibilityRole="button"
                onPress={handleDismiss}
                style={[styles.button, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={[styles.buttonText, { fontSize: FS_MD * fontScale, color: theme.textSecondary, ...ffs }]}
                >
                  {t('common.dismiss')}
                </Text>
              </TouchableOpacity>
            )}

            {message.actionType && texts.action !== '' && (
              <TouchableOpacity
                testID="message-modal-action"
                accessibilityLabel={texts.action}
                accessibilityRole="button"
                onPress={handleAction}
                style={[styles.button, styles.primaryButton, { backgroundColor: theme.primary }]}
              >
                <Text
                  allowFontScaling={false}
                  style={[styles.buttonText, { fontSize: FS_MD * fontScale, color: '#FFFFFF', ...ffs }]}
                >
                  {texts.action}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 360,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  body: {
    lineHeight: 20,
    marginBottom: 24,
    textAlign: 'center',
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
    minWidth: 100,
    alignItems: 'center',
  },
  primaryButton: {
    borderWidth: 0,
  },
  buttonText: {
    fontWeight: '500',
  },
});
